/**
 * TypeScript view of the `picross` format.
 * Source of truth: `shared/schema/picross.{definition,submission,solution,progress}.json`.
 */

export type Localized = Record<string, string | undefined>;

/** Lengths of the filled runs along one line, in order. Empty for a blank line. */
export type Clue = number[];

export interface PicrossDefinition {
  puzzleType: "picross";
  typeVersion: number;
  rows: number;
  cols: number;
  clues: { rows: Clue[]; cols: Clue[] };
  rulesText?: Localized;
  meta?: Record<string, unknown>;
}

export const FILLED = "#";
export const EMPTY = ".";
/** A solving aid: the player is sure the cell stays empty. Never sent to the server. */
export const CROSSED = "x";
export type CellState = typeof FILLED | typeof EMPTY | typeof CROSSED;

/** Autosave shape. Opaque to the core; this type owns it and its migrations. */
export interface PicrossProgress {
  version: 1;
  /** One string per row, `#` filled, `x` crossed, `.` undecided. */
  rows: string[];
}

/** One string per row, `#` filled and `.` empty: crosses are folded into `.`. */
export interface PicrossSubmission {
  rows: string[];
}

/** The author's picture, in the submission format. */
export type PicrossSolution = PicrossSubmission;

export const MAX_SIZE = 50;

export type Axis = "row" | "col";

export function cellId(row: number, col: number): string {
  return `r${row}c${col}`;
}

/** Picks the viewer's language, falling back to the base language, then English, then any. */
export function localized(value: Localized | null | undefined, locale: string): string {
  if (!value) return "";
  return (
    value[locale] ??
    value[locale.split("-")[0]] ??
    value.en ??
    Object.values(value).find((v) => typeof v === "string" && v.length > 0) ??
    ""
  );
}
